import React, { useState, useEffect, useCallback, useRef } from "react";
import { PrimaryBlueButton, Severity } from "@velaro/velaro-component-lib";
import useToastAlert from "@/hooks/useToastAlert";
import LoadingSpinner from "@/components/LoadingSpinner";
import { ScraperJob, ScrapedPage, CreateScraperJobRequest, AzureIndex } from "./types";
import CreateScraperJobModal from "./CreateScraperJobModal";
import ScraperJobRow from "./ScraperJobRow";
import ScraperJobResultsModal from "./ScraperJobResultsModal";
import PublishToIndexModal from "./PublishToIndexModal";
import useSite from "@/hooks/useSite";
import useAzureIndexes from "@/hooks/useAzureIndexes";
import useAzureIndexesApi from "@/hooks/useAzureIndexesApi";
import FileUpload from "@/components/FileUpload/FileUpload";
import FilesEditor from "@/components/FileUpload/FilesEditor";

const SCRAPER_API_URL = import.meta.env.VITE_SCRAPER_API_URL || "http://localhost:8000/api/v1";
const SCRAPER_API_KEY = import.meta.env.VITE_SCRAPER_API_KEY || "";

type TabType = "web" | "file";

export default function AINewTraining() {
  const [activeTab, setActiveTab] = useState<TabType>("web");
  const [allJobs, setAllJobs] = useState<ScraperJob[]>([]); // Cache all jobs
  const [newFiles, setNewFiles] = useState<File[]>([]);
  const [uploading, setUploading] = useState(false);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [selectedJob, setSelectedJob] = useState<ScraperJob | null>(null);
  const [jobResults, setJobResults] = useState<ScrapedPage[] | null>(null);
  const [showResultsModal, setShowResultsModal] = useState(false);
  const [showPublishModal, setShowPublishModal] = useState(false);
  const [publishingJob, setPublishingJob] = useState<ScraperJob | null>(null);
  const toastAlert = useToastAlert();
  const { site } = useSite();
  const { azureIndexes } = useAzureIndexes();
  const { publishJob } = useAzureIndexesApi();
  const jobsRef = useRef<ScraperJob[]>([]);

  // Filter jobs based on active tab (client-side filtering, no refetch)
  const jobs = React.useMemo(() => {
    return activeTab === "web"
      ? allJobs.filter((job) => Number(job.total_urls) > 0)
      : allJobs.filter((job) => Number(job.total_urls) === 0);
  }, [allJobs, activeTab]);

  // Keep ref in sync with ALL jobs for polling (must check all jobs regardless of active tab)
  jobsRef.current = allJobs;

  // Fetch headers for Python scraper API (JSON requests)
  const getHeaders = useCallback(() => {
    return {
      "Content-Type": "application/json",
      "X-Service-Key": SCRAPER_API_KEY,
      "X-Site-Id": site?.siteId?.toString() || "0"
    };
  }, [site?.siteId]);

  // Upload headers (no Content-Type - let browser set multipart boundary)
  const getUploadHeaders = useCallback(() => {
    return {
      "X-Service-Key": SCRAPER_API_KEY,
      "X-Site-Id": site?.siteId?.toString() || "0"
    };
  }, [site?.siteId]);

  // Fetch all jobs (no filtering - done client-side)
  const fetchJobs = useCallback(async () => {
    try {
      const response = await fetch(`${SCRAPER_API_URL}/jobs`, {
        headers: getHeaders()
      });
      if (response.ok) {
        const data = await response.json();
        setAllJobs(data); // Store all jobs, filtering happens in useMemo
      } else {
        console.error("Failed to fetch jobs:", response.status);
      }
    } catch (error) {
      console.error("Error fetching jobs:", error);
    } finally {
      setLoading(false);
    }
  }, [getHeaders]);

  // Initial fetch on mount (no refetch on tab change - filtering is client-side)
  useEffect(() => {
    // Only fetch if site is loaded (siteId > 0)
    if (site?.siteId && site.siteId > 0) {
      setLoading(true);
      fetchJobs();
    } else {
      setLoading(false);
    }
  }, [site?.siteId, fetchJobs]);

  // Polling for running jobs - uses ref to avoid stale closure and infinite loop
  useEffect(() => {
    const interval = setInterval(() => {
      const hasRunningJobs = jobsRef.current.some(
        j => j.status === "running" || j.status === "pending"
      );
      if (hasRunningJobs) {
        fetchJobs();
      }
    }, 5000);

    return () => clearInterval(interval);
  }, [fetchJobs]);

  // Create new scraper job
  const createJob = async (request: CreateScraperJobRequest) => {
    try {
      const response = await fetch(`${SCRAPER_API_URL}/scrape`, {
        method: "POST",
        headers: getHeaders(),
        body: JSON.stringify(request)
      });

      if (response.ok) {
        const data = await response.json();
        toastAlert.displayToast(Severity.Success, `Job "${data.name}" created successfully`);
        setShowCreateModal(false);
        fetchJobs();
      } else {
        const error = await response.json();
        toastAlert.displayToast(Severity.Error, error.detail || "Failed to create job");
      }
    } catch (error) {
      console.error("Error creating job:", error);
      toastAlert.displayToast(Severity.Error, "Failed to create scraper job");
    }
  };

  // View job results
  const viewResults = async (job: ScraperJob) => {
    if (job.status !== "completed" && job.status !== "failed") {
      toastAlert.displayToast(Severity.Warning, "Job is still in progress");
      return;
    }

    try {
      const response = await fetch(`${SCRAPER_API_URL}/jobs/${job.job_id}/results`, {
        headers: getHeaders()
      });

      if (response.ok) {
        const data = await response.json();
        setSelectedJob(job);
        setJobResults(data.results);
        setShowResultsModal(true);
      } else {
        toastAlert.displayToast(Severity.Error, "Failed to fetch results");
      }
    } catch (error) {
      console.error("Error fetching results:", error);
      toastAlert.displayToast(Severity.Error, "Failed to fetch job results");
    }
  };

  // Delete job
  const deleteJob = async (job: ScraperJob) => {
    if (!confirm(`Are you sure you want to delete job "${job.name}"?`)) {
      return;
    }

    try {
      const response = await fetch(`${SCRAPER_API_URL}/jobs/${job.job_id}`, {
        method: "DELETE",
        headers: getHeaders()
      });

      if (response.ok) {
        toastAlert.displayToast(Severity.Success, `Job "${job.name}" deleted`);
        fetchJobs();
      } else {
        toastAlert.displayToast(Severity.Error, "Failed to delete job");
      }
    } catch (error) {
      console.error("Error deleting job:", error);
      toastAlert.displayToast(Severity.Error, "Failed to delete job");
    }
  };

  // Upload files to scraper service (parallel uploads)
  const uploadFiles = async () => {
    if (newFiles.length === 0) return;

    setUploading(true);

    // Upload all files in parallel
    const uploadPromises = newFiles.map(async (file) => {
      const formData = new FormData();
      formData.append("file", file);
      formData.append("name", file.name);

      try {
        const response = await fetch(`${SCRAPER_API_URL}/upload`, {
          method: "POST",
          headers: getUploadHeaders(),
          body: formData
        });

        if (response.ok) {
          return { success: true, file: file.name };
        } else {
          const error = await response.json();
          console.error(`Failed to upload ${file.name}:`, error);
          return { success: false, file: file.name };
        }
      } catch (error) {
        console.error(`Error uploading ${file.name}:`, error);
        return { success: false, file: file.name };
      }
    });

    const results = await Promise.all(uploadPromises);
    const successCount = results.filter(r => r.success).length;
    const errorCount = results.filter(r => !r.success).length;

    setUploading(false);
    setNewFiles([]);

    if (successCount > 0) {
      toastAlert.displayToast(
        Severity.Success,
        `Successfully uploaded ${successCount} file(s)`
      );
      fetchJobs();
    }

    if (errorCount > 0) {
      toastAlert.displayToast(
        Severity.Error,
        `Failed to upload ${errorCount} file(s)`
      );
    }
  };

  // View extracted content from file
  const viewFileContent = async (job: ScraperJob) => {
    if (job.status !== "completed") {
      toastAlert.displayToast(Severity.Warning, "File is still processing");
      return;
    }

    try {
      const response = await fetch(`${SCRAPER_API_URL}/jobs/${job.job_id}/results`, {
        headers: getHeaders()
      });

      if (response.ok) {
        const data = await response.json();
        if (data.results && data.results[0]) {
          const content = data.results[0].content;
          // Escape HTML to prevent XSS
          const escapeHtml = (text: string) => {
            const div = document.createElement('div');
            div.textContent = text;
            return div.innerHTML;
          };
          const win = window.open("", "_blank");
          if (win) {
            win.document.write(`
              <html>
                <head>
                  <title>${escapeHtml(job.name)} - Extracted Content</title>
                  <style>
                    body { font-family: Arial, sans-serif; padding: 20px; max-width: 900px; margin: 0 auto; }
                    pre { white-space: pre-wrap; word-wrap: break-word; background: #f5f5f5; padding: 15px; border-radius: 5px; }
                  </style>
                </head>
                <body>
                  <h1>${escapeHtml(job.name)}</h1>
                  <p><strong>Extracted Content:</strong></p>
                  <pre>${escapeHtml(content)}</pre>
                </body>
              </html>
            `);
            win.document.close();
          }
        }
      } else {
        toastAlert.displayToast(Severity.Error, "Failed to fetch content");
      }
    } catch (error) {
      console.error("Error fetching content:", error);
      toastAlert.displayToast(Severity.Error, "Failed to fetch content");
    }
  };

  // Resume job
  const resumeJob = async (job: ScraperJob) => {
    try {
      const response = await fetch(`${SCRAPER_API_URL}/jobs/${job.job_id}/resume`, {
        method: "POST",
        headers: getHeaders()
      });

      if (response.ok) {
        toastAlert.displayToast(Severity.Success, `Job "${job.name}" resumed`);
        fetchJobs();
      } else {
        const error = await response.json();
        toastAlert.displayToast(Severity.Error, error.detail || "Failed to resume job");
      }
    } catch (error) {
      console.error("Error resuming job:", error);
      toastAlert.displayToast(Severity.Error, "Failed to resume job");
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <LoadingSpinner />
      </div>
    );
  }

  return (
    <div className="p-4">
      {/* Tabs */}
      <div className="border-b border-slate-200 mb-6">
        <nav className="-mb-px flex space-x-8">
          <button
            onClick={() => setActiveTab("web")}
            className={`${
              activeTab === "web"
                ? "border-blue-500 text-blue-600"
                : "border-transparent text-slate-500 hover:text-slate-700 hover:border-slate-300"
            } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm`}
          >
            Web Training
          </button>
          <button
            onClick={() => setActiveTab("file")}
            className={`${
              activeTab === "file"
                ? "border-blue-500 text-blue-600"
                : "border-transparent text-slate-500 hover:text-slate-700 hover:border-slate-300"
            } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm`}
          >
            File Training
          </button>
        </nav>
      </div>

      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <div>
          <h2 className="text-xl font-semibold">
            {activeTab === "web" ? "Web Scraper Jobs" : "File Training"}
          </h2>
          <p className="text-sm text-slate-500">
            {activeTab === "web"
              ? "Create and manage web scraping jobs for AI training data"
              : "Upload and process files for AI training"}
          </p>
        </div>
        {activeTab === "web" && (
          <PrimaryBlueButton
            label="New Scraper Job"
            onClick={() => setShowCreateModal(true)}
          />
        )}
      </div>

      {/* File Upload Section */}
      {activeTab === "file" && (
        <div className="mb-6">
          <FileUpload onAddFiles={setNewFiles} multiple>
            <div className="flex flex-row text-slate-500 text-xs">
              Supported files: PDF, DOCX, DOC, XLSX, XLS, CSV, TXT, JPG, JPEG, PNG
            </div>
            <div className="flex flex-row text-slate-500 text-xs">
              Files will be processed and text content extracted for AI training
            </div>
          </FileUpload>
          <FilesEditor files={newFiles} onUpdate={setNewFiles} />
          {newFiles.length > 0 && (
            <div className="flex justify-end mt-4">
              <PrimaryBlueButton
                label={uploading ? "Uploading..." : "Upload"}
                disabled={uploading}
                onClick={uploadFiles}
              />
            </div>
          )}
        </div>
      )}

      {/* Jobs List */}
      {jobs.length === 0 ? (
        <div className="text-center py-12 bg-slate-50 rounded-lg">
          <p className="text-slate-500 mb-4">
            {activeTab === "web" ? "No scraper jobs yet" : "No files uploaded yet"}
          </p>
          {activeTab === "web" && (
            <PrimaryBlueButton
              label="Create Your First Job"
              onClick={() => setShowCreateModal(true)}
            />
          )}
        </div>
      ) : activeTab === "file" ? (
        // File Training Table
        <div className="bg-white rounded-lg border">
          <table className="w-full">
            <thead className="bg-slate-50 border-b">
              <tr>
                <th className="text-left p-3 text-sm font-medium text-slate-600">File Name</th>
                <th className="text-left p-3 text-sm font-medium text-slate-600">Status</th>
                <th className="text-left p-3 text-sm font-medium text-slate-600">Uploaded</th>
                <th className="text-right p-3 text-sm font-medium text-slate-600">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {jobs.map((job) => (
                <tr key={job.job_id} className="hover:bg-slate-50">
                  <td className="p-3">
                    <span className="text-sm font-medium">{job.name}</span>
                  </td>
                  <td className="p-3">
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                      job.status === "completed" ? "bg-green-100 text-green-800" :
                      job.status === "failed" ? "bg-red-100 text-red-800" :
                      "bg-blue-100 text-blue-800"
                    }`}>
                      {job.status}
                    </span>
                  </td>
                  <td className="p-3">
                    <span className="text-sm text-slate-600">
                      {new Date(job.created_at).toLocaleDateString()}
                    </span>
                  </td>
                  <td className="p-3 text-right">
                    <div className="flex justify-end gap-2">
                      {job.status === "completed" && (
                        <button
                          onClick={() => viewFileContent(job)}
                          className="px-3 py-1 text-sm text-blue-600 hover:bg-blue-50 rounded"
                        >
                          View Content
                        </button>
                      )}
                      <button
                        onClick={() => deleteJob(job)}
                        className="px-3 py-1 text-sm text-red-600 hover:bg-red-50 rounded"
                      >
                        Delete
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        // Web Training Table
        <div className="bg-white rounded-lg border">
          <table className="w-full">
            <thead className="bg-slate-50 border-b">
              <tr>
                <th className="text-left p-3 text-sm font-medium text-slate-600">Name</th>
                <th className="text-left p-3 text-sm font-medium text-slate-600">Status</th>
                <th className="text-left p-3 text-sm font-medium text-slate-600">Progress</th>
                <th className="text-left p-3 text-sm font-medium text-slate-600">Scraped</th>
                <th className="text-left p-3 text-sm font-medium text-slate-600">Created</th>
                <th className="text-right p-3 text-sm font-medium text-slate-600">Actions</th>
              </tr>
            </thead>
            <tbody>
              {jobs.map((job) => (
                <ScraperJobRow
                  key={job.job_id}
                  job={job}
                  onViewResults={() => viewResults(job)}
                  onDelete={() => deleteJob(job)}
                  onResume={() => resumeJob(job)}
                  onPublish={() => {
                    setPublishingJob(job);
                    setShowPublishModal(true);
                  }}
                />
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Create Job Modal */}
      {showCreateModal && (
        <CreateScraperJobModal
          onClose={() => setShowCreateModal(false)}
          onCreate={createJob}
        />
      )}

      {/* Results Modal */}
      {showResultsModal && selectedJob && jobResults && (
        <ScraperJobResultsModal
          job={selectedJob}
          results={jobResults}
          onClose={() => {
            setShowResultsModal(false);
            setSelectedJob(null);
            setJobResults(null);
          }}
        />
      )}

      {/* Publish to Index Modal */}
      {showPublishModal && publishingJob && (
        <PublishToIndexModal
          job={publishingJob}
          indexes={azureIndexes}
          onClose={() => {
            setShowPublishModal(false);
            setPublishingJob(null);
          }}
          onPublish={async (indexId: number) => {
            const result = await publishJob(indexId, publishingJob.job_id);
            if (result.success) {
              toastAlert.displayToast(
                Severity.Success,
                `Published "${publishingJob.name}" to index successfully`
              );
            } else {
              toastAlert.displayToast(
                Severity.Error,
                result.error || "Failed to publish job to index"
              );
            }
          }}
        />
      )}
    </div>
  );
}
